import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import { useLazyQuery } from '@apollo/client'
import {
  Collapse,
  Fade,
  List,
  ListItem,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline'
import SearchIcon from '@material-ui/icons/Search'
import { Skeleton } from '@material-ui/lab'
import { matchSorter } from 'match-sorter'
import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import noDataImage from '../../assets/images/EmptyState.svg'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { TEMPLATES_QUERY } from '../../graphql/contractTemplates'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { convertPersianNumbersToEnglishNumbers } from '../../helpers/numbers'
import { getEntityCode, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import contractPagesRoleManagement from '../../staticRoleManagement/contractRoleManagement'
import { Accommodation } from '../../types/accommodation'
import { TemplateBrief } from '../../types/contractTemplate'
import { ProviderBrief } from '../../types/provider'
import useStyles from './styles/ContractFormStepOne.styles'

interface ContractFormStepOneProps {
  selectedTemplate: string
  setselectedTemplate: (c: string) => void
  providerCodeForGenerate: string
  setProviderCodeForGenerate: (c: string) => void
  isRoleProvider: boolean
  isRoleAccommodation: boolean
}
const ContractFormStepOne: React.FC<ContractFormStepOneProps> = ({
  selectedTemplate,
  setselectedTemplate,
  providerCodeForGenerate,
  setProviderCodeForGenerate,
  isRoleProvider,
  isRoleAccommodation
}) => {
  const classes = useStyles()
  const [showItems, setshowItems] = useState(false)
  const [searchInputValue, setsearchInputValue] = useState<string>('')
  const [filteredTemplates, setfilteredTemplates] = useState<TemplateBrief[]>(
    []
  )
  const { providercode, templateID } = useParams<{
    providercode: string
    templateID: string
  }>()

  const [getProviders, { data: providerData, loading: loadingForProvider }] =
    useLazyQuery<{
      providers: ProviderBrief[]
    }>(PROVIDERS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    setshowItems(true)
  }, [])

  const [
    getContractTemplates,
    { data: templates, loading: loadingForTemplates }
  ] = useLazyQuery<{
    templates: TemplateBrief[]
  }>(TEMPLATES_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [autoCompleteLabelForProvider, setAutoCompleteLabelForProvider] =
    useState<any>({})

  useEffect(() => {
    if (!isRoleProvider && !isRoleAccommodation) {
      getProviders()
    }
  }, [])

  const [
    getAccommodations,
    { data: accommodationData, loading: loadingForAccommodation }
  ] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      setProviderCodeForGenerate(
        accommodationData?.accommodations[0].basicDetails.defaultProviderCode ||
          ''
      )
    }
  })

  useEffect(() => {
    if (isRoleAccommodation) {
      getAccommodations()
    }
    if (isRoleProvider) {
      setProviderCodeForGenerate(getEntityCode() || '')
    }
  }, [])

  useEffect(() => {
    if (isRoleProvider) {
      if (providerCodeForGenerate || templateID) {
        getContractTemplates({
          variables: {
            providerCode: templateID
              ? autoCompleteLabelForProvider.value
              : providerCodeForGenerate
          }
        })
      }
    } else if (
      (autoCompleteLabelForProvider.value &&
        autoCompleteLabelForProvider.label) ||
      (providerCodeForGenerate !== '' &&
        !isRoleAccommodation &&
        !isRoleProvider)
    ) {
      getContractTemplates({
        variables: {
          providerCode: templateID
            ? autoCompleteLabelForProvider.value
            : providerCodeForGenerate
        }
      })
      if (templateID !== null) {
        setselectedTemplate(templateID)
      }
    }
  }, [autoCompleteLabelForProvider.value, providerCodeForGenerate, templates])

  useEffect(() => {
    const fullTemplatesList = templates?.templates ? templates?.templates : []
    const newFilteredTemplates = matchSorter(
      fullTemplatesList,
      searchInputValue,
      {
        keys: [
          'Title',
          {
            key: 'Title',
            threshold: matchSorter.rankings.STARTS_WITH
          }
        ]
      }
    )
    setfilteredTemplates(newFilteredTemplates)
  }, [
    searchInputValue,
    loadingForTemplates,
    autoCompleteLabelForProvider.value,
    templates
  ])

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const hasAccessToChangeProvider = contractPagesRoleManagement
    .find((item) => item.route === '/contracts/(list|templates)')
    ?.sections?.PROVIDER_SELECTOR?.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  return (
    <div>
      {hasAccessToChangeProvider && (
        <AutocompleteWithLabel
          placeholder={getTranslate('Please select a provider')}
          label={getTranslate('Provider')}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.code === value.code}
          options={
            providerData?.providers?.map((item) => ({
              label: item.name,
              value: item.providerCode.toString() || '',
              id: item.id || '',
              code: item.displayProviderCode
            })) || []
          }
          onChange={(_, value: any) => {
            if (providercode && value) {
              setAutoCompleteLabelForProvider({
                value: value.value,
                label: value.label,
                code: value.code
              })
            } else if (value) {
              setAutoCompleteLabelForProvider({
                value: value.value,
                label: value.label,
                code: value.code
              })
              setProviderCodeForGenerate(value.value)
            }
          }}
          value={autoCompleteLabelForProvider}
          fullWidth
          noHelperText
          inputSize="small"
          disabled={providercode !== undefined}
          style={{ marginBottom: 18 }}
          loading={loadingForProvider}
          loadingText={getTranslate('Loading') + '...'}
        />
      )}
      {!loadingForTemplates &&
        (providerCodeForGenerate && autoCompleteLabelForProvider !== null ? (
          <InputWithLabel
            placeholder={getTranslate('Search by templates name')}
            autoComplete="off"
            noHelperText
            fullWidth
            startIcon={<SearchIcon />}
            className={classes.searchInput}
            value={searchInputValue}
            onChange={(event) => {
              setsearchInputValue(
                convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
              )
            }}
          />
        ) : !loadingForTemplates &&
          !loadingForProvider &&
          providerCodeForGenerate &&
          filteredTemplates.length === 0 ? (
          <div className={classes.buttonsPositionSolver}>
            <img src={noDataImage} alt="" />
            <div className={classes.noDataInfoContainer}>
              <Typography className={classes.noDataInfo} variant="subtitle1">
                {getTranslate('No Data')}
              </Typography>
              <Typography className={classes.noDataInfo} variant="subtitle1">
                {getTranslate('No Template available, try adding one')}
              </Typography>
              <div className={classes.createTemplateLinkContainer}>
                <AddCircleOutlineIcon
                  className={classes.createTemplateIcon}
                  color="primary"
                />
                <LocalizedLink
                  className={classes.createTemplateLink}
                  style={{
                    textDecoration: 'underline',
                    color: theme.palette.primary.main
                  }}
                  to="/templates/list/create">
                  <Typography color="primary" variant="h4">
                    {getTranslate(
                      isSmallScreen ? 'Create new' : 'Create new Template'
                    )}
                  </Typography>
                </LocalizedLink>
              </div>
            </div>
          </div>
        ) : (
          <div className={classes.buttonsPositionSolver}></div>
        ))}

      <List className={classes.templateContainer}>
        {loadingForTemplates || loadingForAccommodation
          ? Array(18)
              .fill('')
              .map((item, index) => (
                <ListItem
                  className={[classes.templateItem].join(' ')}
                  key={`accommodation-skeleton${item}${index.toString()}`}>
                  <Skeleton className={classes.skeleton} />
                </ListItem>
              ))
          : filteredTemplates.map((singleContractTemplate, index) => (
              <Collapse in={showItems} key={index}>
                <ListItem
                  button
                  className={[
                    classes.templateItem,
                    selectedTemplate === singleContractTemplate.Id
                      ? classes.activeTemplateItem
                      : ''
                  ].join(' ')}
                  onClick={() => {
                    if (providercode === undefined)
                      setselectedTemplate(singleContractTemplate.Id)
                  }}>
                  <Fade
                    in={selectedTemplate === singleContractTemplate.Id}
                    timeout={250}>
                    <span className={classes.activeCircle} />
                  </Fade>
                  {singleContractTemplate.Title}
                </ListItem>
              </Collapse>
            ))}
      </List>
    </div>
  )
}

export default ContractFormStepOne
